import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useToast } from "../../../../../_context/ToastContext/ToastContext";
import { useDynamicForm } from "../../../../../_components/form/hooks/useDynamicForm";
import { useUserManagementFormSchema } from "./useUserManagementFormSchema";
import { useUserManagementFormMutations } from "./useUserManagementFormMutations";
import { formatUserSuccessMessage } from "../utils/userManagementFormRules";

/**
 * State Management Layer - mirrors every other migrated modal's
 * use*FormState.js: the dynamic-form engine + close-confirmation dismiss
 * guard + submit orchestration. Directly replaces the old modal's
 * hand-rolled hasUnsavedChanges/originalFormData deep-compare (3 separate
 * effects) and manual ESC-key listener with react-hook-form's own
 * formState.isDirty.
 */
export function useUserManagementFormState({ mode, rowData, isOpen, onClose, onSuccess, refetchFilters }) {
  const { showToast } = useToast();
  const { submitUser, isSubmitting } = useUserManagementFormMutations();
  const currentOrgId = useSelector((state) => state.auth.user?.selectedOrg);

  const rawSchema = useUserManagementFormSchema({ mode, rowData });
  const dynamicForm = useDynamicForm(rawSchema, { mode, rowData });
  const {
    handleSubmit: rhfHandleSubmit,
    reset,
    formState: { isDirty },
  } = dynamicForm;

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowCloseConfirmation(false);
    }
  }, [isOpen]);

  function resetAndClose() {
    reset();
    setShowCloseConfirmation(false);
    onClose();
  }

  function attemptClose() {
    if (isDirty) {
      setShowCloseConfirmation(true);
    } else {
      resetAndClose();
    }
  }

  function cancelDiscard() {
    setShowCloseConfirmation(false);
  }

  const onSubmit = rhfHandleSubmit(async (validData) => {
    try {
      const response = await submitUser({ mode, validData, rowData, currentOrgId });

      showToast(formatUserSuccessMessage(validData, mode), "success");
      onSuccess?.(response);
      refetchFilters?.();
      resetAndClose();
    } catch (error) {
      showToast(error?.data?.message || "Something went wrong. Please try again.", "error");
    }
  });

  return {
    ...dynamicForm,
    schema: rawSchema,
    showCloseConfirmation,
    hasUnsavedChanges: isDirty,
    isSubmitting,
    attemptClose,
    confirmDiscard: resetAndClose,
    cancelDiscard,
    onSubmit,
  };
}
